import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, Sparkles, ArrowRight } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from "@/components/ui/sheet";
import { useLaunchBoardroom } from "@/lib/onboarding";
import { cn } from "@/lib/utils";

const NAV_LINKS = [
  { label: "Product", href: "#product" },
  { label: "Agents", href: "#agents" },
  { label: "Roadmap", href: "#roadmap" },
  { label: "FAQ", href: "#faq" },
];

export default function LandingNav() {
  const [scrolled, setScrolled] = useState(false);
  const launch = useLaunchBoardroom();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleSignIn = () => navigate("/login");

  return (
    <header
      data-testid="landing-nav"
      data-scrolled={scrolled ? "true" : "false"}
      className={cn(
        "fixed inset-x-0 top-0 z-50 transition-all duration-300",
        scrolled
          ? "border-b border-black/[0.04] bg-white/70 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent",
      )}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 md:px-8">
        <Link
          to="/"
          data-testid="landing-nav-brand"
          className="flex items-center gap-2.5 outline-none"
        >
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-zinc-900 to-black">
            <Sparkles className="h-4 w-4 text-white" strokeWidth={2.4} />
          </div>
          <div className="text-[15px] font-extrabold tracking-tight">
            Idea AI
          </div>
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`landing-nav-link-${l.label.toLowerCase()}`}
              className="text-[13px] font-semibold text-zinc-600 transition-colors hover:text-black"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          <button
            type="button"
            data-testid="landing-nav-signin"
            onClick={handleSignIn}
            className="text-[13px] font-semibold text-zinc-700 transition-colors hover:text-black"
          >
            Sign in
          </button>
          <Link
            to="/boardroom"
            data-testid="landing-nav-cta"
            onClick={(e) => {
              e.preventDefault();
              launch();
            }}
            className="group inline-flex items-center gap-1.5 rounded-full bg-black px-4 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-zinc-800"
          >
            Launch boardroom
            <ArrowRight
              className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5"
              strokeWidth={2.4}
            />
          </Link>
        </div>

        {/* Mobile */}
        <Sheet>
          <SheetTrigger asChild>
            <button
              type="button"
              data-testid="landing-nav-mobile-trigger"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-black/[0.06] bg-white text-zinc-700 md:hidden"
              aria-label="Open menu"
            >
              <Menu className="h-4 w-4" />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="w-full bg-white sm:max-w-sm">
            <SheetTitle className="sr-only">Menu</SheetTitle>
            <div className="flex flex-col gap-4 pt-8">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  className="text-[18px] font-bold text-zinc-900"
                >
                  {l.label}
                </a>
              ))}
              <div className="mt-4 flex flex-col gap-2">
                <button
                  type="button"
                  onClick={handleSignIn}
                  className="rounded-full border border-black/[0.06] bg-white px-4 py-3 text-center text-[14px] font-semibold text-zinc-800"
                >
                  Sign in
                </button>
                <Link
                  to="/boardroom"
                  onClick={(e) => {
                    e.preventDefault();
                    launch();
                  }}
                  className="rounded-full bg-black px-4 py-3 text-center text-[14px] font-semibold text-white"
                >
                  Launch boardroom →
                </Link>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
